import { useState } from "react";
import { track } from "../lib/track";

const CATEGORIES = [
  { id: "working-well", label: "Working well", emoji: "👍" },
  { id: "confusing",    label: "Confusing",    emoji: "🤔" },
  { id: "feature-idea", label: "Feature idea", emoji: "💡" },
  { id: "bug",          label: "Bug",          emoji: "🐛" },
];

interface Props {
  mode: string;
  userEmail?: string | null;
  onClose: () => void;
}

export default function FeedbackModal({ mode, userEmail, onClose }: Props) {
  const [category, setCategory] = useState<string | null>(null);
  const [message,  setMessage]  = useState("");
  const [status,   setStatus]   = useState<"idle" | "sending" | "sent" | "error">("idle");

  const formUrl = (import.meta.env.VITE_FEEDBACK_FORM_URL as string | undefined)?.trim();

  async function handleSubmit() {
    if (!category || message.trim().length < 5 || status !== "idle") return;
    setStatus("sending");

    if (!formUrl) {
      setStatus("error");
      return;
    }

    try {
      const cat = CATEGORIES.find(c => c.id === category)!;
      const res = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject:  `[MoneyMission Feedback] ${cat.label}`,
          category:  cat.label,
          message:   message.trim(),
          mode,
          ...(userEmail ? { _replyto: userEmail } : {}),
        }),
      });
      if (res.ok) track("feedback_submitted", { category: cat.label });
      setStatus(res.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  }

  const canSubmit = !!category && message.trim().length >= 5 && status === "idle";

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "20px 20px 0 0",
          width: "100%", maxWidth: 480,
          maxHeight: "92vh", overflowY: "auto",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.18)",
          paddingBottom: 24,
        }}
      >
        {status === "sent" ? (
          <div style={{ textAlign: "center", padding: "48px 24px" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🙏</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>Thanks!</div>
            <div style={{ fontSize: 14, color: "#475569", marginBottom: 28, lineHeight: 1.5 }}>
              Your feedback helps make MoneyMission better for everyone.
            </div>
            <button onClick={onClose} style={btnStyle("#0ea5e9", true)}>Done</button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 20px 0" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Give Feedback</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Help us improve MoneyMission</div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: "#f1f5f9", border: "none", borderRadius: 20,
                  width: 32, height: 32, cursor: "pointer", fontSize: 14,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, marginTop: 2,
                }}
              >
                ✕
              </button>
            </div>

            {/* Category */}
            <div style={{ padding: "18px 20px 0" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                What kind of feedback?
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    style={{
                      padding: "10px 14px",
                      border: `2px solid ${category === c.id ? "#0ea5e9" : "#e2e8f0"}`,
                      borderRadius: 12, cursor: "pointer",
                      background: category === c.id ? "#eff6ff" : "#f8fafc",
                      display: "flex", alignItems: "center", gap: 8,
                      transition: "all 0.15s",
                      minHeight: 48,
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{c.emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: category === c.id ? "#0369a1" : "#374151", textAlign: "left" }}>
                      {c.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div style={{ padding: "16px 20px 0" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                Tell us more
              </div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value.slice(0, 500))}
                placeholder="What's on your mind?"
                style={{
                  width: "100%", minHeight: 100, padding: 12,
                  borderRadius: 12, border: "2px solid #e2e8f0",
                  resize: "none", fontSize: 14, fontFamily: "inherit",
                  color: "#1e293b", background: "#f8fafc", outline: "none",
                  boxSizing: "border-box", lineHeight: 1.5,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "#0ea5e9"; }}
                onBlur={e  => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
              />
              <div style={{ textAlign: "right", fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                {message.length}/500
              </div>
            </div>

            {/* Error */}
            {status === "error" && (
              <div style={{ margin: "8px 20px 0", padding: "10px 14px", background: "#fef2f2", borderRadius: 10, fontSize: 13, color: "#dc2626" }}>
                {formUrl
                  ? "Something went wrong — please try again."
                  : "Feedback isn't configured yet. Check back soon!"}
              </div>
            )}

            {/* Submit */}
            <div style={{ padding: "16px 20px 0" }}>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{ ...btnStyle("#0ea5e9", canSubmit), width: "100%" }}
              >
                {status === "sending" ? "Sending…" : "Send Feedback"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function btnStyle(bg: string, active: boolean): React.CSSProperties {
  return {
    background: active ? bg : "#cbd5e1",
    border: "none", borderRadius: 12,
    padding: "14px 20px",
    fontSize: 15, fontWeight: 800, color: "#fff",
    cursor: active ? "pointer" : "default",
    opacity: active ? 1 : 0.7,
    transition: "background 0.15s, opacity 0.15s",
    minHeight: 48,
  };
}
